import { InfoTip } from '@dev-spendesk/grapes';
import isNil from 'lodash/isNil';
import { useState } from 'react';

import { CardAddress } from 'common/components/CardAddress/CardAddress';
import { useTranslation } from 'common/hooks/useTranslation';
import { useCompany } from 'modules/app/hooks/useCompany';
import { CardInactiveOverlay } from 'modules/cards/components/Cards/Overlay/CardInactiveOverlay';
import { CardLoading } from 'modules/cards/components/Cards/Overlay/CardLoading';
import { PhysicalCard } from 'modules/cards/components/Cards/PhysicalCard';
import appConfig from 'src/core/config';
import { formatCardExpirationDate } from 'src/core/modules/cards/models/formatCardExpirationDate';
import { formatMoney } from 'src/core/utils/money';

import { PlasticCardActions } from './PlasticCardActions';
import { type ApiCard } from '../../../card';
import { getVisibleNumbersShortened } from '../../../utils/card';

type Props = {
  card: ApiCard;
  syncActivity: () => void;
};

export const PlasticCardHeader = ({ card, syncActivity }: Props) => {
  const { t } = useTranslation('global');

  const company = useCompany();
  const [isCardBeingBlockedOrUnblocked, setIsCardBeingBlockedOrUnblocked] =
    useState(false);

  return (
    <div className="rounded-12 bg-background-secondary-default mb-16 flex items-center justify-between p-48">
      <div className="flex flex-col items-center">
        <div className="relative isolate w-[316px]">
          {(card.deleted_at ||
            (card.status !== 'ACT' && !isCardBeingBlockedOrUnblocked)) && (
            <CardInactiveOverlay
              cardState={card.deleted_at ? 'cancelled' : card.activation_status}
            />
          )}
          {isCardBeingBlockedOrUnblocked && <CardLoading />}
          <PhysicalCard
            numbers={getVisibleNumbersShortened(
              card.card_visible_number,
              card.status,
            )}
            expiryDate={formatCardExpirationDate(card.card_exp_date)}
            amount={
              card.status !== 'PRE'
                ? formatMoney(card.available, company.currency)
                : undefined
            }
            brand={
              appConfig.environmentName === 'demo'
                ? 'visa'
                : (card.brand as 'visa' | 'mastercard')
            }
            amountTooltip={t('cards.panel.availableTooltip')}
          />
        </div>

        <PlasticCardActions
          card={card}
          syncActivity={syncActivity}
          isCardBeingBlockedOrUnblocked={isCardBeingBlockedOrUnblocked}
          setIsCardBeingBlockedOrUnblocked={setIsCardBeingBlockedOrUnblocked}
        />
      </div>
      <div className="ml-48 w-[464px] min-w-100">
        {!isNil(card.monthly_budget) && (
          <div className="text-content-secondary-bg-secondary body-s mb-24 flex items-center gap-4">
            {t('cards.monthlyBudgetWithAmount', {
              amount: formatMoney(card.monthly_budget, company.currency),
            })}
            <InfoTip content={t('cards.monthlyBudgetTip')} />
          </div>
        )}
        <CardAddress cardId={card.id} />
      </div>
    </div>
  );
};
